<template>
    <div>
        <div class="card mb-3" id="rankingCriteriaEditCard" :class="readonly ? 'border-primary' : 'border-success'" :style="{ border: readonly ? 'none' : 'solid'}">
            <!--{{model}}-->
            <div class="card-header">
                <div class="row">
                    <div class="col-sm-12 col-md-6 text-success">
                        <h5>{{model.name}}</h5>
                    </div>
                    <div class="text-right col-sm-12 col-md-6">
                        <div v-if="showBtns" class="btn-group" role="group" aria-label="User edit buttons">
                            <button v-if="!readonly" type="button" class="btn btn-success" :disabled="saving" @click.stop="onSave">
                                <i v-if="saving" class="fas fa-spinner fa-spin"></i>
                                <i v-else class="fas fa-save mr-2"> </i>
                                {{ $t('buttons.save.btnText')}}
                            </button>
                            <button v-if="!readonly" type="button" class="btn btn-danger" :disabled="saving" @click.stop="onCancel"><i class="fas fa-ban mr-2"></i>{{ $t('buttons.cancel')}}</button>
                            <button v-if="readonly" type="button" class="btn btn-danger" :disabled="saving" @click.stop="onCancel"><i class="fas fa-times mr-2"></i>{{ $t('buttons.close')}}</button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card-body">
                <div class="row">
                    <div class="col-form-label form-group col-sm-12 col-md-6">
                        <label class="font-weight-bold required" :for="'name' + _uid">{{$t('rankingCriteria.editor.name')}}</label>
                        <input type="text"
                               class="form-control"
                               v-model="model.name"
                               v-validate="'required'"
                               :data-vv-as="$t('rankingCriteria.editor.name')"
                               :name="'name' + _uid"
                               :id="'name' + _uid"
                               :disabled="readonly" />
                        <span class="text-danger" v-if="errors.has('name' + _uid)">{{ errors.first('name' + _uid) }}</span>
                    </div>

                    <div class="form-group col-sm-12 col-md-6">
                        <label class="col-form-label font-weight-bold" :for="'nameEn' + _uid">{{ $t('rankingCriteria.editor.nameEn') }}</label>
                        <input type="text"
                               class="form-control"
                               v-model="model.nameEn"
                               :name="'nameEn' + _uid"
                               :id="'nameEn' + _uid"
                               :disabled="readonly" />
                    </div>

                    <div class="form-group col-sm-12 col-md-6">
                        <label class="col-form-label font-weight-bold" :for="'other1' + _uid">{{ $t('rankingCriteria.editor.other1') }}</label>
                        <input type="text"
                               class="form-control"
                               v-model="model.other1"
                               :name="'other1' + _uid"
                               :id="'other1' + _uid"
                               :disabled="readonly" />
                    </div>

                    <div class="form-group col-sm-12 col-md-6">
                        <label class="col-form-label font-weight-bold" :for="'other2' + _uid">{{ $t('rankingCriteria.editor.other2') }}</label>
                        <input type="text"
                               class="form-control"
                               v-model="model.other2"
                               :name="'other2' + _uid"
                               :id="'other2' + _uid"
                               :disabled="readonly" />
                    </div>

                    <div class="col-form-label form-group col-12">
                        <label class="font-weight-bold" :for="'eventForm' + _uid">{{$t('rankingCriteria.editor.eventForm')}}</label>
                        <NomenclatureMultiDropdown
                            :loadByParentSort="true"
                            :parentSort="eventFormSort"
                            v-model="model.eventFormIds"
                            :fieldName="$t('rankingCriteria.editor.eventForm')"
                            :bindPopup="true"
                            :popupContainerId="'#rankingCriteriaEditCard'"
                            :disabled="readonly">
                        </NomenclatureMultiDropdown>
                    </div>
                </div>

                <div class="form-group mt-3 mb-3">
                    <vs-checkbox v-model="model.deactivated"
                                 :name="'deactivated' + _uid"
                                 :id="'deactivated' + _uid"
                                 :disabled="readonly"
                                 style="justify-content: flex-start !important">
                        {{ $t('rankingCriteria.editor.deactivated') }}
                    </vs-checkbox>
                </div>

                <hr />

                <div class="card">
                    <h5 class="card-header text-success">{{ $t('rankingCriteria.editor.rules') }}</h5>
                    <div class="card-body">
                        <div class="form-group">
                            <vs-checkbox v-model="model.rankingData.rule.firstInTimeFirstInLaw"
                                         :name="'firstInTimeFirstInLaw' + _uid"
                                         :id="'firstInTimeFirstInLaw' + _uid"
                                         :disabled="readonly"
                                         style="justify-content: flex-start !important">
                                {{ $t('rankingCriteria.editor.firstInTimeFirstInLaw') }}
                            </vs-checkbox>
                        </div>
                    </div>
                </div>

                <hr />

                <div class="card">
                    <h5 class="card-header text-success">{{ $t('rankingCriteria.editor.exceptions') }}</h5>
                    <div class="card-body">
                        <div class="form-group">
                            <vs-checkbox v-model="model.rankingData.exception.manuallyAdded"
                                         :name="'manuallyAdded' + _uid"
                                         :id="'manuallyAdded' + _uid"
                                         :disabled="readonly"
                                         style="justify-content: flex-start !important">
                                {{ $t('rankingCriteria.editor.manuallyAdded') }}
                            </vs-checkbox>
                        </div>
                    </div>
                </div>

                <hr />

                <div class="card">
                    <h5 class="card-header text-success">{{ `${$t('rankingCriteria.editor.constraints')} / ${$t('rankingCriteria.editor.evaluations')}` }}</h5>
                    <div class="card-body" id="constraint-container">
                        <div class="form-group">
                            <div v-if="hasConstraints">
                                <div v-for="constraint in model.rankingData.constraints"
                                     :key="constraint.uid">
                                    <RankingConstraintEditor :value="constraint"
                                                             :readonly="readonly"
                                                             :eventId="model.registerEntityId"
                                                             @delete="onConstraintDelete">
                                    </RankingConstraintEditor>
                                </div>
                            </div>
                            <div>
                                <button v-if="!readonly" type="button" class="btn btn-success" @click.stop="addConstaint"><i class="fas fa-plus mr-2"></i>{{ $t('buttons.add.btnText')}}</button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="card">
                    <h5 class="card-header text-success">{{ $t('rankingCriteria.editor.quotas') }}</h5>
                    <div class="card-body">
                        <div class="form-group">
                            <div v-if="hasQuotas">
                                <div v-for="quota in model.rankingData.quotas"
                                     :key="quota.uid">
                                    <RankingQuotaEditor :value="quota"
                                                        :readonly="readonly"
                                                        :eventId="model.registerEntityId"
                                                        @delete="onQuotaDelete">
                                    </RankingQuotaEditor>
                                </div>
                            </div>
                            <div>
                                <button v-if="!readonly" type="button" class="btn btn-success" @click.stop="addQuota"><i class="fas fa-plus mr-2"></i>{{ $t('buttons.add.btnText')}}</button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row mt-3" v-if="showBtns">
                    <div class="col-sm-12 col-md-6">
                        <div v-if="isEventRankingCriteria && (claims.rankingCriteria.create || claims.rankingCriteriaRegister.create || claims.rankingCriteria.update || claims.rankingCriteriaRegister.update)"
                             class="mt-2">
                            <vs-checkbox v-model="model.copyToRegister"
                                         :name="'copyToRegister' + _uid"
                                         :id="'copyToRegister' + _uid"
                                         :disabled="readonly"
                                         aria-describedby="copyToRegisterHelpBlock"
                                         style="justify-content: flex-start !important">
                                {{ $t('rankingCriteria.editor.copyToRegister') }}
                            </vs-checkbox>
                            <small id="copyToRegisterHelpBlock" class="form-text text-muted">
                                {{ $t('rankingCriteria.help.copyToRegister') }}
                            </small>
                        </div>
                    </div>
                    <div class="text-right col-sm-12 col-md-6">
                        <div class="btn-group" role="group" aria-label="User edit buttons">
                            <button v-if="!readonly" type="button" class="btn btn-success" :disabled="saving" @click.stop="onSave">
                                <i v-if="saving" class="fas fa-spinner fa-spin"></i>
                                <i v-else class="fas fa-save mr-2"> </i>
                                {{ $t('buttons.save.btnText')}}
                            </button>
                            <button v-if="!readonly" type="button" class="btn btn-danger" :disabled="saving" @click.stop="onCancel"><i class="fas fa-ban mr-2"></i>{{ $t('buttons.cancel')}}</button>
                            <button v-if="readonly" type="button" class="btn btn-danger" :disabled="saving" @click.stop="onCancel"><i class="fas fa-times mr-2"></i>{{ $t('buttons.close')}}</button>
                        </div>
                    </div>
                </div>

            </div>

            <div class="card-footer text-muted" v-if="!isNew">
                <span class="m-1">
                    <span v-if="model.creatorUsername">{{ $t('createdBy') }} <strong>{{ model.creatorUsername }}</strong></span>
                    <span v-if="model.createdAtUtc">{{ ' - ' + this.$moment(model.createdAtUtc).format('DD.MM.YYYY HH:mm:ss') }}</span>
                </span>
                <span class="m-1">
                    <span v-if="model.updaterUsername">{{ ' / ' + $t('lastUpdatedBy') }} <strong>{{ model.updaterUsername }}</strong></span>
                    <span v-if="model.updatedAtUtc">{{ ' - ' + this.$moment(model.updatedAtUtc).format('DD.MM.YYYY HH:mm:ss') }}</span>
                </span>
            </div>
        </div>
    </div>
</template>

<script>
    import { RankingCriteria, RankingConstraint, RankingQuota } from '../../models/models';
    import { NomenclatureParentBySort } from "../../models/enums";
    import RankingConstraintEditor from './RankingConstraintEditor';
    import RankingQuotaEditor from './RankingQuotaEditor';
    import service from '../../services/rankingCriteria.service';
    import NomenclatureMultiDropdown from "@/components/dropdown/MultiSelect";
    import { mapGetters } from 'vuex';

    export default {
        inject: ['$validator'],
        props: {
            value: {
                type: RankingCriteria,
                required: false
            },
            createNew: {
                type: Boolean,
                default: false
            },
            readonly: {
                type: Boolean,
                default: true
            },
            showBtns: {
                type: Boolean,
                default: true
            },
        },
        components: {
            RankingConstraintEditor,
            RankingQuotaEditor,
            NomenclatureMultiDropdown
        },
        created() {
        },
        data() {
            return {
                model: this.value,
                saving: false,
                eventFormSort: NomenclatureParentBySort.EventForm,
            }
        },
        methods: {
            onCancel() {
                this.$emit("cancel");
            },
            onSave() {
                this.$validator.validateAll().then(result => {
                    if (result) {
                        kendo.confirm(`${this.$t(this.isNew ? 'rankingCriteria.confirmations.create' : 'rankingCriteria.confirmations.edit')} '${this.model.name}'?`)
                            .then(function () {
                                this.doSave();
                            }.bind(this));
                    }
                });
            },
            doSave() {
                if (this.isNew) {
                    this.create();
                } else {
                    this.update();
                }
            },
            create() {
                this.saving = true;

                service
                    .create(this.model)
                    .then(response => {
                        this.$emit("save");

                        this.$vs.notify({
                            title: this.$t('buttons.create.action'),
                            text: this.$helpers.responseMessage(response),
                            color: 'success',
                            time: this.$store.getters.notificationDuration,
                        })
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('buttons.create.action'),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    }).then(() => { this.saving = false });
            },
            update() {
                this.saving = true;

                service
                    .update(this.model)
                    .then(response => {
                        this.$emit("save");

                        this.$vs.notify({
                            title: this.$t('buttons.edit.action'),
                            text: this.$helpers.responseMessage(response),
                            color: 'success',
                            time: this.$store.getters.notificationDuration,
                        })
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('buttons.edit.action'),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    }).then(() => { this.saving = false });
            },
            addConstaint() {
                if (this.model && this.model.rankingData) {
                    this.model.rankingData.constraints.push(new RankingConstraint({ uid: this.$helpers.getUid() }))
                }
            },
            addQuota() {
                if (this.model && this.model.rankingData) {
                    this.model.rankingData.quotas.push(new RankingQuota({ uid: this.$helpers.getUid() }))
                }
            },
            onConstraintDelete(constraint) {
                if (this.hasConstraints) {
                    this.model.rankingData.constraints.splice(this.model.rankingData.constraints.indexOf(constraint), 1);
                }
            },
            onQuotaDelete(quota) {
                if (this.hasQuotas) {
                    this.model.rankingData.quotas.splice(this.model.rankingData.quotas.indexOf(quota), 1);
                }
            }
        },
        computed: {
            ...mapGetters(['claims', 'isEnVersion']),
            isNew() {
                if (!this.model.id && !this.model.registerEntityId) {
                    return true;
                }

                return false;
            },
            hasConstraints() {
                return this.model && this.model.rankingData && this.model.rankingData.constraints && this.model.rankingData.constraints.length > 0;
            },
            hasQuotas() {
                return this.model && this.model.rankingData && this.model.rankingData.quotas && this.model.rankingData.quotas.length > 0;
            },
            isEventRankingCriteria() {
                return this.model && this.model.registerEntityId;
            }
        },
        mounted() {

        },
        watch: {
            value: function (val) {
                if (val) {
                    this.model = new RankingCriteria(this.value);
                }
            }
        }
    }
</script>

<style scoped>
    input[readonly] {
        background-color: whitesmoke;
    }

    textarea[readonly] {
        background-color: whitesmoke;
    }
</style>
